import type { SessionMode, TimerState } from '@aj/core';
import {
  abandonSession,
  completeSession,
  elapsedMinutes,
  elapsedMs,
  pauseTimer,
  resumeTimer,
  startTimer,
} from '@aj/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppData } from '../data/AppDataProvider.js';

/**
 * A running focus session.
 *
 * Deliberately NOT in the event log (see timer.ts): ticks would write
 * thousands of events a day. It lives in one device-local record and emits
 * exactly one event when it ends.
 *
 * Two things this hook exists to guarantee:
 *
 *  • **The session survives the app dying.** It is mirrored to localStorage on
 *    every transition, and elapsed time is derived from the wall clock, so a
 *    tab killed by Android at minute 12 restores at minute 12 — or at minute
 *    40 if that is how long the student was actually gone. A student who loses
 *    fifty minutes to a memory reclaim uninstalls.
 *  • **The screen stays awake.** A timer that lets the phone sleep is a timer
 *    a student stops trusting, and the wake lock is dropped the instant the
 *    session ends so it can never leak.
 */

const STORAGE_KEY = 'aj.session';

/** How often the countdown repaints. Display only — nothing is accumulated. */
const TICK_MS = 1000;

function load(): TimerState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimerState;
    // A stored record from an older shape would poison every read after it.
    return typeof parsed?.sessionId === 'string' && typeof parsed.startedAt === 'number'
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function save(timer: TimerState | null): void {
  try {
    if (timer) localStorage.setItem(STORAGE_KEY, JSON.stringify(timer));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A private-mode browser that refuses storage still gets a working timer
    // for as long as the tab lives. Losing persistence is not losing the app.
  }
}

export type StartInput = {
  track: string;
  planMinutes: number;
  mode?: SessionMode;
  intention?: string;
};

export function useFocusSession() {
  const { actions } = useAppData();
  const [timer, setTimer] = useState<TimerState | null>(load);
  const [now, setNow] = useState(() => Date.now());
  const wakeLock = useRef<WakeLockSentinel | null>(null);

  const running = timer !== null && timer.pausedAt === null;

  // Repaint once a second while running. The interval drives the DISPLAY only
  // — elapsed time is always recomputed from `startedAt`, so a throttled or
  // suspended tab produces a stale screen, never a wrong total.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [running]);

  // Coming back to a backgrounded tab must correct the clock immediately
  // rather than on the next tick.
  useEffect(() => {
    const onVisible = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => save(timer), [timer]);

  // The wake lock follows `running` and is released on unmount, so ending,
  // pausing, or navigating away all drop it.
  useEffect(() => {
    let cancelled = false;

    const release = () => {
      void wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
    };

    if (running && 'wakeLock' in navigator) {
      void navigator.wakeLock
        .request('screen')
        .then((sentinel) => {
          if (cancelled) void sentinel.release().catch(() => undefined);
          else wakeLock.current = sentinel;
        })
        // Denied on a backgrounded tab and on browsers without it. A session
        // without a wake lock is worse, not broken.
        .catch(() => undefined);
    } else {
      release();
    }

    return () => {
      cancelled = true;
      release();
    };
  }, [running]);

  const start = useCallback((input: StartInput) => {
    const at = Date.now();
    setNow(at);
    setTimer(
      startTimer({
        sessionId: `s${at}`,
        track: input.track,
        mode: input.mode ?? 'focus',
        planMinutes: input.planMinutes,
        now: at,
        ...(input.intention === undefined ? {} : { intention: input.intention }),
      }),
    );
  }, []);

  const pause = useCallback(() => setTimer((t) => (t ? pauseTimer(t, Date.now()) : t)), []);
  const resume = useCallback(() => {
    setNow(Date.now());
    setTimer((t) => (t ? resumeTimer(t, Date.now()) : t));
  }, []);

  /**
   * Finish, writing the entry.
   *
   * A session under a minute produces nothing — there is no honest entry to
   * write for forty seconds, and one would only teach that the timer is a way
   * to manufacture credit.
   */
  const finish = useCallback(
    async (wrap: { nextStep?: string; recall?: string } = {}) => {
      const current = timer;
      setTimer(null);
      if (!current) return;
      if (elapsedMinutes(current, Date.now()) < 1) return;

      await actions.run((_, ctx) => ({
        ok: true,
        events: [
          completeSession(
            current,
            {
              eventId: ctx.newId(),
              entryId: ctx.newId(),
              now: ctx.now,
              tzOffsetMinutes: ctx.tzOffsetMinutes,
              studyDate: ctx.today,
            },
            wrap,
          ),
        ],
      }));
    },
    [timer, actions],
  );

  /** Bail out. Recorded honestly, earns nothing, never counts as activity. */
  const abandon = useCallback(async () => {
    const current = timer;
    setTimer(null);
    if (!current) return;

    await actions.run((_, ctx) => ({
      ok: true,
      events: [
        abandonSession(current, {
          eventId: ctx.newId(),
          entryId: ctx.newId(),
          now: ctx.now,
          tzOffsetMinutes: ctx.tzOffsetMinutes,
          studyDate: ctx.today,
        }),
      ],
    }));
  }, [timer, actions]);

  return {
    timer,
    running,
    now,
    elapsed: timer ? elapsedMinutes(timer, now) : 0,
    /** Seconds remaining against the plan. Negative once it overruns. */
    remainingSeconds: timer
      ? timer.planMinutes * 60 - Math.floor(elapsedMs(timer, now) / 1000)
      : 0,
    start,
    pause,
    resume,
    finish,
    abandon,
  };
}
